/**
 * Tenant → Properties. The list; rows drill into the property page without a
 * document navigation — the shell stays mounted, which is the entire point.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, request, type Property } from '../api';
import { useSession } from '../session';
import { AddressInput } from '../components/AddressInput';
import { StateSelect } from '../components/StateSelect';

const CAN_MANAGE = new Set(['compliance_lead', 'regional_manager']);

export function Properties() {
  const { me } = useSession();
  const canManage = CAN_MANAGE.has(me.user.role);
  const navigate = useNavigate();

  const [properties, setProperties] = useState<Property[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [creating, setCreating] = useState(false);

  function load() {
    return request<{ properties: Property[] }>('/properties')
      .then((data) => setProperties(data.properties))
      .catch(() => setFailed(true));
  }

  useEffect(() => {
    void load();
  }, []);

  const totalUnits = (properties ?? []).reduce((n, p) => n + p.unitCount, 0);

  return (
    <>
      <div className="page__head">
        <div>
          <h1>Properties</h1>
          <p className="sub">
            {properties
              ? `${properties.length} propert${properties.length === 1 ? 'y' : 'ies'} · ${totalUnits} units`
              : '—'}
          </p>
        </div>
        <div className="spacer" />
        {canManage && (
          <button className="btn btn--primary" onClick={() => setCreating(true)}>
            New property
          </button>
        )}
      </div>

      {creating && (
        <NewPropertyPanel
          onDone={() => {
            setCreating(false);
            void load();
          }}
          onCancel={() => setCreating(false)}
        />
      )}

      <div className="panel">
        <div className="panel__head">
          <h2>Your properties</h2>
          <div className="spacer" />
          <span className="tag tag--grey">{properties ? `${properties.length} total` : '–'}</span>
        </div>
        <div className="table-scroll">
          <table className="tbl tbl--click">
            <thead>
              <tr>
                <th>Property</th>
                <th>City</th>
                <th>County</th>
                <th>State #</th>
                <th>Units</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {failed && (
                <tr>
                  <td colSpan={7} className="dim" style={{ padding: '22px 13px' }}>
                    Could not load properties.
                  </td>
                </tr>
              )}
              {properties && properties.length === 0 && (
                <tr>
                  <td colSpan={7} className="dim" style={{ padding: '22px 13px' }}>
                    {canManage ? 'No properties yet. Create the first one above.' : 'No properties yet.'}
                  </td>
                </tr>
              )}
              {(properties ?? []).map((p) => (
                <tr key={p.id} onClick={() => navigate(`/properties/${p.id}`)}>
                  <td className="strong">{p.name}</td>
                  <td className="dim">{p.city ?? '—'}</td>
                  <td className="dim">{p.county ?? '—'}</td>
                  <td className="dim">{p.stateProjectNumber ?? '—'}</td>
                  <td className="num">
                    {p.unitCount}
                    {p.totalUnits ? ` / ${p.totalUnits}` : ''}
                  </td>
                  <td>
                    <span className={`tag tag--${p.status === 'active' ? 'ok' : 'grey'}`}>{p.status}</span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span className="dim">Open ›</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="panel__foot">
          {canManage
            ? 'Click a property to manage its profile and units.'
            : 'Click a property to see its profile and units. Creating properties is a compliance-lead ability.'}
        </div>
      </div>
    </>
  );
}

const FUNDING_OPTIONS: Array<[string, string]> = [
  ['lihtc_9', 'LIHTC 9%'],
  ['lihtc_4', 'LIHTC 4% / bond'],
  ['home', 'HOME'],
  ['section_8_pbra', 'Section 8 PBRA'],
];

function NewPropertyPanel({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const [fields, setFields] = useState<Record<string, string>>({});
  const [funding, setFunding] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const set = (key: string) => (value: string) => setFields((f) => ({ ...f, [key]: value }));
  const bind = (key: string) => ({
    value: fields[key] ?? '',
    onChange: (e: { target: { value: string } }) => set(key)(e.target.value),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);

    const payload: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(fields)) {
      if (value.trim() === '') continue;
      if (key === 'year_built' || key === 'total_units') payload[key] = Number(value);
      else payload[key] = value.trim();
    }
    if (funding.length) payload.funding_types = funding;

    request('/properties', { method: 'POST', body: payload })
      .then(onDone)
      .catch((err) => {
        setError(
          err instanceof ApiError && err.code === 'name_required'
            ? 'A property name is required.'
            : 'Could not create the property.',
        );
        setBusy(false);
      });
  }

  return (
    <div className="panel" style={{ marginBottom: 14 }}>
      <div className="panel__head">
        <h2>New property</h2>
        <div className="spacer" />
        <button className="btn btn--sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
      <div className="panel__body">
        <form className="form" style={{ maxWidth: 'none' }} onSubmit={onSubmit}>
          <div className="field--row">
            <div className="field">
              <label htmlFor="np-name">Property name</label>
              <input id="np-name" type="text" required placeholder="Riverbend Terrace" {...bind('name')} />
            </div>
            <div className="field">
              <label htmlFor="np-spn">State project number</label>
              <input id="np-spn" type="text" placeholder="IL-2024-00123" spellCheck={false} {...bind('state_project_number')} />
              <span className="hint">Your monitoring agency’s identifier — annual reporting is keyed by it.</span>
            </div>
          </div>

          <div className="field--row">
            <div className="field">
              <label htmlFor="np-address1">Address</label>
              <AddressInput
                id="np-address1"
                value={fields.address_line1 ?? ''}
                onChange={set('address_line1')}
                onPlace={(place) =>
                  setFields((f) => ({
                    ...f,
                    address_line1: place.line1 || (f.address_line1 ?? ''),
                    city: place.city || (f.city ?? ''),
                    state: place.state || (f.state ?? ''),
                    postal_code: place.zip || (f.postal_code ?? ''),
                    county: place.county || (f.county ?? ''),
                  }))
                }
              />
            </div>
            <div className="field">
              <label htmlFor="np-address2">Line 2</label>
              <input id="np-address2" type="text" {...bind('address_line2')} />
            </div>
          </div>

          <div className="field--row">
            <div className="field">
              <label htmlFor="np-city">City</label>
              <input id="np-city" type="text" {...bind('city')} />
            </div>
            <div className="field--row">
              <div className="field">
                <label htmlFor="np-state">State</label>
                <StateSelect id="np-state" {...bind('state')} />
              </div>
              <div className="field">
                <label htmlFor="np-zip">ZIP</label>
                <input id="np-zip" type="text" inputMode="numeric" {...bind('postal_code')} />
              </div>
            </div>
          </div>

          <div className="field--row">
            <div className="field">
              <label htmlFor="np-county">County</label>
              <input id="np-county" type="text" {...bind('county')} />
              <span className="hint">Income and rent limits are published per county.</span>
            </div>
            <div className="field--row">
              <div className="field">
                <label htmlFor="np-phone">Phone</label>
                <input id="np-phone" type="tel" {...bind('phone')} />
              </div>
              <div className="field">
                <label htmlFor="np-fax">Fax</label>
                <input id="np-fax" type="tel" {...bind('fax')} />
              </div>
            </div>
          </div>

          <div className="field--row">
            <div className="field">
              <label htmlFor="np-owner">Owner entity</label>
              <input id="np-owner" type="text" placeholder="Riverbend Terrace LLC" {...bind('owner_llc_name')} />
              <span className="hint">The single-asset LLC — what Form 8609 is filed under.</span>
            </div>
            <div className="field">
              <label htmlFor="np-owner-ein">Owner EIN / TIN</label>
              <input id="np-owner-ein" type="text" spellCheck={false} {...bind('owner_ein')} />
            </div>
          </div>

          <div className="field--row">
            <div className="field">
              <label htmlFor="np-year">Year built</label>
              <input id="np-year" type="number" min={1800} max={2100} {...bind('year_built')} />
            </div>
            <div className="field--row">
              <div className="field">
                <label htmlFor="np-total">Planned units</label>
                <input id="np-total" type="number" min={0} {...bind('total_units')} />
              </div>
              <div className="field">
                <label htmlFor="np-agency">Monitoring agency</label>
                <input id="np-agency" type="text" placeholder="IHDA" {...bind('monitoring_agency')} />
              </div>
            </div>
          </div>

          <div className="field">
            <label>Programs</label>
            <div className="check-grid">
              {FUNDING_OPTIONS.map(([value, label]) => (
                <label key={value} className="check">
                  <input
                    type="checkbox"
                    checked={funding.includes(value)}
                    onChange={(e) =>
                      setFunding((f) => (e.target.checked ? [...f, value] : f.filter((x) => x !== value)))
                    }
                  />{' '}
                  {label}
                </label>
              ))}
            </div>
          </div>

          {error && <p className="login__error">{error}</p>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn--primary" type="submit" disabled={busy}>
              {busy ? 'Creating…' : 'Create property'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
